/**
 * Card (deliverable) capability booleans for the caller, derived from the
 * permission slugs `/api/me` serializes on `membership.permissions`.
 *
 * Gate on SLUGS, not on `membership.role`. A tenant admin can edit any
 * system role's permission set in Settings → Role Management, so a
 * role-derived switch (the interim pattern in
 * `src/features/wiki/lib/permissions.ts` and the chat conversation list)
 * goes silently wrong the moment a tenant customizes one. The BE gates on
 * the same set it serializes here, so the two sides cannot drift.
 *
 * Fall-open contract, matching the `allowsTimer` convention on
 * `CardTimerButton`: an ABSENT `permissions` array means an older BE that
 * doesn't serialize the field yet (or `useMe` still in flight), so every
 * capability reads `true` and the BE's 403 remains the backstop — a
 * FE-first deploy is a no-op rather than a lockout. A PRESENT but empty
 * array is a real answer and falls closed.
 *
 * Call the hook once per page or modal and pass the booleans down as
 * props. Never call it per row — a board renders hundreds of cards.
 */

import { useMemo } from 'react'

import type { Me } from '@/api/types-bridge'
import { useMe } from './use-me'

export interface CardPermissions {
  /** Create a card, clone one, or spawn the next recurrence. */
  canCreateCards: boolean
  /** Edit any card field, move a card between columns, manage attendees. */
  canEditCards: boolean
  /** Delete a card. Bound to admin + owner only. */
  canDeleteCards: boolean
  /** Post a comment on a card. The `commenter` role has this WITHOUT edit. */
  canCommentCards: boolean
}

const ALL_ALLOWED: CardPermissions = {
  canCreateCards: true,
  canEditCards: true,
  canDeleteCards: true,
  canCommentCards: true,
}

export function cardPermissionsFromMe(me: Me | undefined): CardPermissions {
  const slugs = me?.membership?.permissions
  if (slugs == null) return ALL_ALLOWED

  return {
    canCreateCards: slugs.includes('create-deliverables'),
    canEditCards: slugs.includes('edit-deliverables'),
    canDeleteCards: slugs.includes('delete-deliverables'),
    canCommentCards: slugs.includes('comment-on-deliverables'),
  }
}

export function useCardPermissions(): CardPermissions {
  const me = useMe()
  return useMemo(() => cardPermissionsFromMe(me.data), [me.data])
}
